<template>
  <div class="order-item" :class="{
    'order-item-printable': isPrintable && !isFullyPrinted,
    'order-item-fully-printed': isPrintable && isFullyPrinted
  }">
    <!-- Item Header Row -->
    <div class="order-item-header">
      <div class="order-item-info">
        <!-- Product name -->
        <div class="order-item-name">
          <strong>{{ displayTitle }}</strong>
        </div>
      </div>

      <div class="order-item-details">
        <!-- Print format badge -->
        <CBadge v-if="isPrintable && printFormat" color="info" class="mr-2">
          {{ printFormat }}
        </CBadge>

        <!-- Finish badge -->
        <CBadge v-if="isPrintable && printConfig && printConfig.finish" color="secondary" class="mr-2">
          {{ printConfig.finish }}
        </CBadge>

        <!-- Quantity badge - only for printable items -->
        <CBadge
          v-if="isPrintable"
          :color="item.quantity > 1 ? 'warning' : 'light'"
          class="mr-2"
        >
          <CIcon v-if="item.quantity > 1" name="cilWarning" size="sm" class="mr-1"/>
          <translate translate-context="edge-prints.item">Qty</translate>
          <span class="ml-1 font-weight-bold">{{ item.quantity }}</span>
        </CBadge>

        <!-- Total prints badge if quantity > 1 (only for printable) -->
        <CBadge v-if="isPrintable && item.quantity > 1 && mediaCounts.total > 0" color="warning" class="mr-2">
          <CIcon name="cilPrint" size="sm" class="mr-1"/>
          <translate translate-context="edge-prints.item"
                    :translate-params="{total: totalPrints}">
            %{total} total prints
          </translate>
        </CBadge>

        <!-- Total price (only for non-bundle items) -->
        <div v-if="hasPerm('edge.print.total') && !isPartOfBundle" class="order-item-price">
          <strong>{{ item.total_price | currency(order.currency) }}</strong>
        </div>

        <!-- Selection buttons (only if has media) -->
        <div v-if="(isPrintable || hasMedia) && mediaCounts.total > 0" class="order-item-selection-buttons ml-3">
        <!-- Select All / Deselect All -->
        <CButton
          v-if="selectedMediaCount < mediaCounts.total"
          color="secondary"
          variant="outline"
          size="sm"
          @click="selectAll"
        >
          <CIcon name="cipCheckbox" class="mr-1"/>
          <translate translate-context="edge-prints.item"
                    :translate-params="{count: mediaCounts.total}">
            Select All (%{count})
          </translate>
        </CButton>

        <CButton
          v-else
          color="secondary"
          variant="outline"
          size="sm"
          @click="deselectAll"
        >
          <CIcon name="cipClose" class="mr-1"/>
          <translate translate-context="edge-prints.item">
            Deselect All
          </translate>
        </CButton>

        <!-- Select To Print with count (only for printable) -->
        <CButton
          v-if="isPrintable && mediaCounts.remaining > 0 && mediaCounts.remaining < mediaCounts.total"
          color="warning"
          variant="outline"
          size="sm"
          @click="selectToPrint"
          class="ml-2"
        >
          <CIcon name="cipClock" class="mr-1"/>
          <span v-if="item.quantity > 1">
            {{ mediaCounts.remaining }} × {{ item.quantity }}
          </span>
          <span v-else>
            <translate translate-context="edge-prints.item"
                      :translate-params="{count: mediaCounts.remaining}">
              Select %{count} to print
            </translate>
          </span>
        </CButton>
        </div>
      </div>
    </div>

    <!-- Media Preview Gallery - Always visible for printable, conditional for digital -->
    <OrderMediaPreview
      v-if="(isPrintable || hasMedia) && hasPerm('edge.print.media.list')"
      :order-item="item"
      :order="order"
      :media-getter="getSortedMediaForOrderItem"
      :selected-media-getter="getSelectedMediaForOrderItem"
      :loading-getter="isMediaLoadingForOrderItem"
      :fetch-media-action="fetchMediaAction"
      :toggle-selection-action="toggleSelectionAction"
      :context-items="contextMenuItems"
      @bulk-select="handleBulkSelect"
      @context-action="handleContextAction"
      @zoom="$emit('zoom', $event)"
      class="mt-2"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import OrderMediaPreview from '@/domain/edgeOrders/shared/OrderMediaPreview.vue'
import Filters from '@/domain/core/utils/filters'

export default {
  name: 'OrderItem',
  components: {
    OrderMediaPreview
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    order: {
      type: Object,
      required: true
    },
    canPrint: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
    }
  },
  computed: {
    ...mapGetters('edgePrints', [
      'getMediaCountsForOrderItem',
      'getSelectedMediaForOrderItem',
      'isProcessingOrder',
      'getMediaForOrderItem',
      'getSortedMediaForOrderItem',
      'isMediaLoadingForOrderItem',
      'getPrintedMediaIdsForOrderItem',
      'getPrintedCopiesForMedia',
      'getRequiredCopiesForOrderItem',
      'getUnprintedMediaIdsForOrderItem'
    ]),

    isPartOfBundle() {
      return !!(this.item.bundle && this.item.bundle.id)
    },

    productName() {
      if (this.item.bundle) {
        return this.item.bundle.name
      } else if (this.item.product) {
        return this.item.product.name
      }
      return '-'
    },

    displayTitle() {
      // Combine product name with variant name if they're different
      const baseName = this.productName
      const variantName = this.item.product_variant?.name

      if (!variantName || variantName === baseName) {
        return baseName
      }

      // If variant name contains product name, just use variant name
      if (variantName.includes(baseName)) {
        return variantName
      }

      // Otherwise combine them
      return `${baseName} - ${variantName}`
    },

    variantBadge() {
      // Extract key variant info that's not already in title or format
      const variant = this.item.product_variant
      if (!variant) return null

      // Look for package/bundle info in description
      const desc = variant.description || ''
      if (desc.toLowerCase().includes('pacchetto')) {
        // Extract package type
        const match = desc.match(/pacchetto\s+(\w+)/i)
        if (match) return match[0]
      }

      return null
    },

    isPrintable() {
      return this.item.is_printable === true
    },

    hasMedia() {
      return (this.item.media_ids && this.item.media_ids.length > 0) ||
             (this.item.gallery_ids && this.item.gallery_ids.length > 0) ||
             (this.item.product?.gallery_ids && this.item.product.gallery_ids.length > 0)
    },

    productDescription() {
      if (this.item.product_variant?.description) {
        return this.item.product_variant.description
      }
      if (this.item.product?.description) {
        return this.item.product.description
      }
      return ''
    },

    printConfig() {
      return this.item.product_variant?.config || this.item.product?.config || null
    },

    printFormat() {
      if (!this.printConfig?.format_cm) return ''
      return this.printConfig.format_cm
    },

    mediaCounts() {
      if (!this.isPrintable) {
        return { total: 0, printed: 0, remaining: 0 }
      }
      return this.getMediaCountsForOrderItem(this.item.id)
    },

    selectedMediaCount() {
      if (!this.isPrintable) return 0
      const selected = this.getSelectedMediaForOrderItem(this.item.id)
      return selected ? selected.length : 0
    },

    totalPrints() {
      // Total number of prints needed (photos × quantity)
      return this.mediaCounts.total * this.item.quantity
    },

    remainingPrints() {
      // For now, assume each printed photo = 1 copy printed
      // TODO: Track actual copies printed per photo
      return this.mediaCounts.remaining * this.item.quantity
    },

    isFullyPrinted() {
      if (!this.isPrintable) return false
      return this.mediaCounts.total > 0 && this.mediaCounts.remaining === 0
    },

    isGalleryBased() {
      // Gallery-based items don't have specific media_ids
      return !this.item.media_ids || this.item.media_ids.length === 0
    },

    contextMenuItems() {
      const items = []

      // Hide and Unlink only for gallery-based items with selections
      if (this.isGalleryBased && this.selectedMediaCount > 0) {
        // Hide (soft delete) - only if has permission
        if (this.hasPerm('edge.print.soft_delete')) {
          items.push({
            value: 'hide',
            label: this.$pgettext('edge-prints.item', 'Hide'),
            icon: 'cilTrash'
          })
        }

        // Unlink - only if has permission
        if (this.hasPerm('edge.print.unlink_pica_code')) {
          items.push({
            value: 'unlink',
            label: this.$pgettext('edge-prints.item', 'Unlink'),
            icon: 'cilLinkBroken'
          })
        }
      }

      // Print action for printable items with selections
      if (this.isPrintable && this.selectedMediaCount > 0) {
        if (items.length > 0) {
          items.push({ divider: true })
        }
        items.push({
          value: 'print',
          label: this.$pgettext('edge-prints.item', 'Print'),
          icon: 'cilPrint'
        })
      }

      return items
    }
  },
  methods: {
    ...mapActions('edgePrints', [
      'selectAllMediaForOrderItem',
      'deselectAllMediaForOrderItem',
      'fetchMediaForOrderItemId',
      'toggleMediaSelectionForOrderItem',
      'hideSelectedMedia',
      'unlinkSelectedMedia'
    ]),

    selectAll() {
      this.selectAllMediaForOrderItem(this.item.id)
    },

    deselectAll() {
      this.deselectAllMediaForOrderItem(this.item.id)
    },

    fetchMediaAction(params) {
      return this.fetchMediaForOrderItemId(params)
    },

    toggleSelectionAction(params) {
      return this.toggleMediaSelectionForOrderItem(params)
    },

    handleBulkSelect({ orderItemId, selectedIds }) {
      this.$store.commit('edgePrints/setOrderItemMediaSelection', {
        orderItemId,
        selectedIds
      })
    },

    selectToPrint() {
      // Use the getter that already calculates unprinted media IDs
      const unprintedIds = this.getUnprintedMediaIdsForOrderItem(this.item.id)

      // Only update selection if there are unprinted items
      if (unprintedIds.length > 0) {
        this.handleBulkSelect({
          orderItemId: this.item.id,
          selectedIds: unprintedIds
        })
      }
    },

    async hideSelectedForItem() {
      // Filter only the selected media for this order item
      const selectedMediaIds = this.getSelectedMediaForOrderItem(this.item.id)

      if (selectedMediaIds.length === 0) {
        return
      }

      await this.confirm({
        title: this.$pgettext('edge-prints.item', 'Hide Selected Photos'),
        message: this.$pgettext('edge-prints.item', 'Are you sure you want to hide the selected photos? They will no longer appear in the gallery but can be restored later.'),
        color: 'danger',
        cb_confirm: async () => {
          try {
            await this.hideSelectedMedia()
            await this.notifySuccess(this.$pgettext('edge-prints.item', 'Photos hidden successfully'))

            // Invalidate cached media to force reload
            this.$store.commit('edgePrints/invalidateOrderItemMedia', { orderItemId: this.item.id })

            // Refresh media for this order item
            await this.fetchMediaAction({
              orderItemId: this.item.id,
              orderItem: this.item,
              picaCode: this.item.picacode || this.order.picacode,
              page: 1
            })
          } catch (error) {
            await this.notifyError(this.$pgettext('edge-prints.item', 'Failed to hide photos'))
          }
        }
      })
    },

    async unlinkSelectedForItem() {
      const selectedMediaIds = this.getSelectedMediaForOrderItem(this.item.id)

      if (selectedMediaIds.length === 0) {
        return
      }

      const picacode = this.order.picacode
      if (!picacode) {
        await this.notifyError(this.$pgettext('edge-prints.item', 'No picacode found for this order'))
        return
      }

      await this.confirm({
        title: this.$pgettext('edge-prints.item', 'Unlink Selected Photos'),
        message: this.$pgettext('edge-prints.item', 'Are you sure you want to unlink the selected photos from this picacode? They will remain in the gallery but no longer associated with this order.'),
        color: 'warning',
        cb_confirm: async () => {
          try {
            await this.unlinkSelectedMedia()
            await this.notifySuccess(this.$pgettext('edge-prints.item', 'Photos unlinked successfully'))

            // Invalidate cached media to force reload
            this.$store.commit('edgePrints/invalidateOrderItemMedia', { orderItemId: this.item.id })

            // Refresh media for this order item
            await this.fetchMediaAction({
              orderItemId: this.item.id,
              orderItem: this.item,
              picaCode: this.item.picacode || this.order.picacode,
              page: 1
            })
          } catch (error) {
            await this.notifyError(this.$pgettext('edge-prints.item', 'Failed to unlink photos'))
          }
        }
      })
    },

    printSelectedForItem() {
      const selectedMediaIds = this.getSelectedMediaForOrderItem(this.item.id)

      if (selectedMediaIds.length === 0) {
        return
      }

      // Create print group for this order item
      const printableGroup = {
        orderItem: this.item,
        order: this.order,
        mediaIds: selectedMediaIds,
        isPrintJobReprint: false
      }

      // Emit to parent to open print summary modal
      this.$emit('open-print-summary', [printableGroup])
    },

    handleContextAction(action) {
      switch (action) {
        case 'hide':
          this.hideSelectedForItem()
          break
        case 'unlink':
          this.unlinkSelectedForItem()
          break
        case 'print':
          this.printSelectedForItem()
          break
      }
    }
  },
  filters: {
    currency: Filters.currency
  }
}
</script>

<style scoped>
.order-item {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  background: white;
  transition: all 0.2s;
}

.order-item-printable {
  border: 2px solid #007bff;
}

.order-item-fully-printed {
  border: 2px solid #28a745;
  background: #f1f9f4;
}

.order-item:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.order-item-printable:hover {
  box-shadow: 0 2px 12px rgba(0,123,255,0.15);
}

.order-item-fully-printed:hover {
  box-shadow: 0 2px 12px rgba(40,167,69,0.15);
}

.order-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.order-item-info {
  flex: 1;
}

.order-item-name {
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.order-item-info {
  display: flex;
  align-items: center;
}

.order-item-details {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.order-item-price {
  font-size: 1.1rem;
  color: #2c3e50;
}

.order-item-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
  border-top: 1px solid #e9ecef;
}

.order-item-photo-info {
  display: flex;
  align-items: center;
}

.order-item-selection-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .order-item-header {
    flex-direction: column;
  }

  .order-item-details {
    margin-top: 0.75rem;
  }
}
</style>